import React from 'react';
import { Grid } from '@material-ui/core';
import { useCommonStyles } from 'components/UI/CommonStyles';
import { useSingleSaleTableListStyles } from 'components/Payments/SingleSale/Table/List/SingleSaleTableList.styles';
import { SingleSaleTableItem } from 'components/Payments/SingleSale/Table/Item/SingleSaleTableItem';
import { GenericOrderItem } from 'components/Payments/SingleSale/SingleSale';
import { FieldError } from '@spike/model';
import { AddOnServiceOrderItem, CustomOrderItem, Order, ProductOrderItem } from '@spike/order-model';
import clsx from 'clsx';

type SingleSaleOrderItem = AddOnServiceOrderItem | CustomOrderItem | ProductOrderItem;

interface RemoveOrderItemPayload {
    orderItem: SingleSaleOrderItem;
    nextItems: Order['items'];
}

interface ItemsTableProps {
    order: Order;
    calculating?: boolean;
    deletingOrderItemId?: number;
    errors?: Array<FieldError>;
    className?: string;
    onChange: (orderItems: Order['items']) => void;
    onRemoveOrderItem?: (payload: RemoveOrderItemPayload) => void;
}

export const SingleSaleTableList: React.FC<ItemsTableProps> = props => {
    const commonClasses = useCommonStyles();
    const classes = useSingleSaleTableListStyles();

    const orderItems = React.useMemo<Array<SingleSaleOrderItem>>(() => {
        return [...props.order.items.products, ...props.order.items.addOnServices, ...props.order.items.customs];
    }, [props.order.items]);

    const orderProductIds = React.useMemo(() => {
        return props.order.items.products.map(item => item.product?.businessProduct?.id).filter(id => id !== undefined);
    }, [props.order.items.products]);

    const orderAddOnServiceIds = React.useMemo(() => {
        return props.order.items.addOnServices.map(item => item.addOnService?.id).filter(id => id !== undefined);
    }, [props.order.items.addOnServices]);

    const changeHandler = (updatedLine: GenericOrderItem) => {
        props.onChange({
            ...props.order.items,
            products: props.order.items.products.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as ProductOrderItem) : item
            ),
            addOnServices: props.order.items.addOnServices.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as AddOnServiceOrderItem) : item
            ),
            customs: props.order.items.customs.map(item =>
                item.orderItem.uuid === updatedLine.orderItem.uuid ? (updatedLine as CustomOrderItem) : item
            )
        });
    };

    const removeHandler = (uuid: string) => {
        const nextItems: Order['items'] = {
            ...props.order.items,
            products: props.order.items.products.filter(item => item.orderItem.uuid !== uuid),
            addOnServices: props.order.items.addOnServices.filter(item => item.orderItem.uuid !== uuid),
            customs: props.order.items.customs.filter(item => item.orderItem.uuid !== uuid)
        };
        const removedItem = orderItems.find(item => item.orderItem.uuid === uuid);

        if (removedItem && props.onRemoveOrderItem) {
            props.onRemoveOrderItem({ orderItem: removedItem, nextItems });
            return;
        }

        props.onChange(nextItems);
    };

    return (
        <>
            <div className={clsx(classes.container, props.className)}>
                <Grid
                    container
                    className={classes.gridHeader}
                >
                    <Grid
                        item
                        xs={2}
                        className={commonClasses.typo15_600}
                    >
                        Item
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Type
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Staff
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Quantity
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Discount
                    </Grid>
                    <Grid
                        item
                        xs={2}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Tax
                    </Grid>
                    <Grid
                        item
                        xs={1}
                        className={clsx(commonClasses.typo15_600, classes.gridCenter)}
                    >
                        Total
                    </Grid>
                    <Grid
                        item
                        xs={1}
                    ></Grid>
                </Grid>

                <div className={classes.tableList}>
                    {orderItems.map(orderItem => {
                        return (
                            <SingleSaleTableItem
                                item={orderItem}
                                errors={props.errors}
                                key={orderItem.orderItem.uuid}
                                selectedProductIds={orderProductIds}
                                selectedAddOnServiceIds={orderAddOnServiceIds}
                                deleting={
                                    props.deletingOrderItemId !== undefined &&
                                    props.deletingOrderItemId === orderItem.orderItem.id
                                }
                                onChange={changeHandler}
                                onRemove={removeHandler}
                            />
                        );
                    })}
                </div>
            </div>
        </>
    );
};
